import { useRef, useCallback, useState, useEffect } from 'react';

/**
 * Custom hook to detect tile hover/focus with optimized performance.
 * Tracks mouse hover and keyboard focus independently — the active index
 * remains set as long as *either* source is active. This prevents a
 * mouseout from clearing the map pin highlight while a tile has keyboard focus.
 *
 * @param {React.RefObject} containerRef - Ref to the container element
 * @param {Array} tileData - Array of tile data (used to trigger cache rebuild)
 * @param {Function} onTileHover - Callback when active tile index changes
 * @param {string} tileSelector - Primary CSS selector for tiles (default: '.tile')
 * @param {string} fallbackContainerSelector - Fallback container selector (default: '.tiles.tiles-location')
 * @returns {Object} - Event handlers for mouse/focus events
 */
const useTileHover = ({
	containerRef,
	tileData,
	onTileHover,
	tileSelector = '.tile',
	fallbackContainerSelector = '.tiles.tiles-location'
}) => {
	const [hoveredIndex, setHoveredIndex] = useState(-1);
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const tilesMapRef = useRef(new Map());
	const lastEmittedRef = useRef(-1);

	// Emit the resolved active index whenever hover or focus changes.
	// Focus takes priority; fall back to hover.
	useEffect(() => {
		const activeIndex = focusedIndex !== -1 ? focusedIndex : hoveredIndex;

		if (activeIndex !== lastEmittedRef.current) {
			lastEmittedRef.current = activeIndex;
			onTileHover(activeIndex);
		}
	}, [hoveredIndex, focusedIndex, onTileHover]);

	// Cache tile elements when tileData changes
	useEffect(() => {
		if (!containerRef?.current) return;

		// Primary selector for tile elements
		let allTiles = containerRef.current.querySelectorAll(tileSelector);

		// Fallback: get children of tiles container
		if (!allTiles.length && fallbackContainerSelector) {
			const tilesContainer = containerRef.current.querySelector(
				fallbackContainerSelector
			);
			if (tilesContainer) {
				allTiles = tilesContainer.children;
			}
		}

		const newMap = new Map();
		Array.from(allTiles).forEach((tile, index) => {
			newMap.set(tile, index);
		});
		tilesMapRef.current = newMap;
	}, [tileData, tileSelector, fallbackContainerSelector, containerRef]);

	/**
	 * Find the tile element from event target
	 */
	const findTileElement = useCallback(
		(target) => {
			// Primary: use closest with tile selector
			let tileElement = target.closest(tileSelector);

			// Fallback: check if target is within a tiles container child
			if (!tileElement && fallbackContainerSelector) {
				const tilesContainer = containerRef?.current?.querySelector(
					fallbackContainerSelector
				);
				if (tilesContainer) {
					tileElement = Array.from(tilesContainer.children).find(
						(child) => child.contains(target)
					);
				}
			}

			return tileElement;
		},
		[tileSelector, fallbackContainerSelector, containerRef]
	);

	/**
	 * Resolve tile index from an event target
	 */
	const resolveTileIndex = useCallback(
		(event) => {
			const tileElement = findTileElement(event.target);
			if (tileElement) {
				const index = tilesMapRef.current.get(tileElement);
				return index !== undefined ? index : -1;
			}
			return -1;
		},
		[findTileElement]
	);

	const handleMouseOver = useCallback(
		(event) => {
			const index = resolveTileIndex(event);
			setHoveredIndex(index);
		},
		[resolveTileIndex]
	);

	const handleMouseLeave = useCallback(() => {
		setHoveredIndex(-1);
	}, []);

	const handleFocus = useCallback(
		(event) => {
			const index = resolveTileIndex(event);
			setFocusedIndex(index);
		},
		[resolveTileIndex]
	);

	const handleBlur = useCallback(() => {
		setFocusedIndex(-1);
	}, []);

	return {
		handleMouseOver,
		handleMouseLeave,
		handleFocus,
		handleBlur
	};
};

export default useTileHover;
